import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Past-due banner — shown at the top of the Dashboard when the user's
 * subscription is past_due. Self-contained: fetches its own state so
 * pages don't need to wire it.
 *
 * Renders nothing in the common (active / none) state so callers can
 * always mount it without conditional layout.
 */
export default function PastDueBanner() {
  const { token } = useAuth();
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API_URL}/api/subscriptions/me`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => setStatus(r.data?.subscription_status))
      .catch(() => setStatus(null));
  }, [token]);

  if (status !== 'past_due') return null;

  return (
    <div
      className="mb-4 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 flex items-start gap-3"
      data-testid="dashboard-past-due-banner"
    >
      <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
      <div className="flex-1">
        <p className="text-sm font-semibold text-amber-200">
          Your last payment didn't go through.
        </p>
        <p className="text-xs text-amber-200/80 mt-0.5">
          Update your card to keep your membership benefits active.
        </p>
      </div>
      <Link
        to="/billing"
        className="text-sm font-semibold text-amber-200 hover:text-amber-100 whitespace-nowrap"
        data-testid="dashboard-past-due-fix-link"
      >
        Fix it →
      </Link>
    </div>
  );
}
